import { Button, ButtonGroup } from '@mui/material';
import { ColDef, GetRowIdParams, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { PortfolioModel } from '../../../../Generated/Commodity-Management-Api';
import { useLoadingState } from '../../../../Hooks';
import { StoneXGrid, StoneXRow } from '../../../StoneX';
import { StoneXModal, StoneXTextField } from '../../../StoneXMui';
import { useSnackbar } from 'notistack';
import { RootState, useApplicationDispatch, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { addPortfolio as addPortfolioRedux, updatePortfolio as updatePortfolioRedux, removePortfolio as removePortfolioRedux } from '../../../../Redux/Slices/CommodityManagementSlice';
import { UserAccessState } from '../../../../Redux/Slices/UserAccessSlice';

interface PortfolioSettingsProps {
  portfolios: PortfolioModel[];
}

export default function PortfolioSettings(props: PortfolioSettingsProps) {

  const dispatch = useApplicationDispatch();
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const { enqueueSnackbar } = useSnackbar();
  const [portfolios, setPortfolios] = useState<PortfolioModel[]>(props.portfolios);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalPortfolio, setModalPortfolio] = useState<PortfolioModel | undefined>();
  const savePortfolioLoadingState = useLoadingState();
  const gridRef = useRef<AgGridReact<PortfolioModel>>(null);

  console.log(portfolios);

  const columnDefs: ColDef[] = [
    {
      headerName: 'Portfolio Name',
      field: 'portfolioName',
      sortable: true,
      flex: 1,
      filter: false,
    },
    {
      headerName: 'Actions',
      width: userAccess.isAdmin ? 240 : 150,
      cellRenderer: (params: any) => {
        return (
          <ButtonGroup variant="text">
            <Button onClick={() => updatePortfolio(params.data)}>Edit</Button>
            <Button onClick={() => togglePortfolioActive(params.data)}>{params.data.active ? 'Disable' : 'Activate'}</Button>
            {userAccess.isAdmin && <Button onClick={() => deletePortfolio(params.data)}>Delete</Button>}
          </ButtonGroup>
        );
      },
    },
  ];

  function savePortfolio(portfolioToSave: Partial<PortfolioModel>) {
    if (!portfolioToSave.portfolioId) {
      CommodityManagementApi.addPortfolio(
        savePortfolioLoadingState.setLoadingState,
        portfolioToSave.portfolioName!,
      ).then((res) => {
        setPortfolios((previous) => [...previous, res.data.portfolio as PortfolioModel]);
        dispatch(addPortfolioRedux({ portfolio: res.data.portfolio! }));
        enqueueSnackbar('Portfolio added successfully', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to add portfolio', { variant: 'error' });
      });
    } else {
      CommodityManagementApi.updatePortfolio(
        savePortfolioLoadingState.setLoadingState,
        portfolioToSave.portfolioId!.toString(),
        portfolioToSave.portfolioName!,
        portfolioToSave.active,
      ).then((res) => {
        setPortfolios((previous) =>
          previous.map((portfolio) =>
            portfolio.portfolioId == res.data.portfolio?.portfolioId ? (res.data.portfolio as PortfolioModel) : portfolio,
          ),
        );
        dispatch(updatePortfolioRedux({ portfolio: res.data.portfolio! }));
        enqueueSnackbar('Portfolio updated successfully', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to update portfolio', { variant: 'error' });
      });
    }
  }

  function deletePortfolio(portfolioToDelete: PortfolioModel) {
    const portfolioToDeleteId = portfolioToDelete.portfolioId!;
    CommodityManagementApi.deletePortfolio(null, portfolioToDeleteId.toString()).then(() => {
      setPortfolios((previous) => previous.filter((portfolio) => portfolio.portfolioId != portfolioToDeleteId));
      dispatch(removePortfolioRedux({ portfolioId: portfolioToDeleteId }));
    });
  }

  function togglePortfolioActive(portfolioToUpdate: PortfolioModel) {
    savePortfolio({ ...portfolioToUpdate, active: !portfolioToUpdate.active });
  }

  function addPortfolio() {
    setModalPortfolio(undefined);
    setModalOpen(true);
  }

  function updatePortfolio(portfolio: PortfolioModel) {
    setModalPortfolio({ ...portfolio });
    setModalOpen(true);
  }

  return (
    <>
      <StoneXRow align="end">
        <h3>Portfolios</h3>
        <Button className="pull-right" variant="outlined" onClick={addPortfolio}>
          Add
        </Button>
      </StoneXRow>
      <div className="ag-theme-alpine" style={{ width: '100%' }}>
        <StoneXGrid
          hideGridOptions
          ref={gridRef}
          getRowId={(params: GetRowIdParams) => params.data.portfolioId}
          columns={columnDefs}
          rows={portfolios}
        />
      </div>
      <UpdatePortfolioModal open={modalOpen} onClose={() => setModalOpen(false)} onPortfolioSave={savePortfolio} portfolio={modalPortfolio} />
    </>
  );
}

interface UpdatePortfolioProps {
  open: boolean;
  onClose: () => void;
  onPortfolioSave: (portfolio: Partial<PortfolioModel>) => void;
  portfolio?: PortfolioModel;
}

function UpdatePortfolioModal(props: UpdatePortfolioProps) {
  const { open, onClose, onPortfolioSave, portfolio } = props;
  const [portfolioName, setPortfolioName] = useState<string>();
  const actionLabel = props.portfolio ? 'Update' : 'Add';

  useEffect(() => {
    setPortfolioName(portfolio?.portfolioName ?? '');
  }, [portfolio]);

  function updatePortfolio() {
    onPortfolioSave({
      portfolioId: portfolio?.portfolioId ?? 0,
      portfolioName: portfolioName,
      active: portfolio?.active
    });

    onClose();
  }

  return (
    <StoneXModal header={actionLabel + ' Portfolio'} open={open} onClose={onClose}>
      <StoneXRow>
        <StoneXTextField label="Portfolio Name" value={portfolioName} onChange={(event: any) => setPortfolioName(event.target.value)} />
      </StoneXRow>

      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={updatePortfolio}>{actionLabel + ' Portfolio'}</Button>
      </StoneXRow>
    </StoneXModal>
  );
}
